import {
    Cached,
    DeleteForeverOutlined,
    UnarchiveOutlined,
} from "@mui/icons-material";
import {
    Alert,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Pagination,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import Authorize from "../Authorize";
import ConfirmDelete from "../ConfirmAction";
import { APIRequestStatus } from "../constants";
import UserContext, { User_Type } from "../UserContext";

type Props = {
    label: string;
    getter: (props: {
        page: number;
        limit: number;
        filter: any;
        org: any;
    }) => Promise<any[]>;
    onDelete: (item: any, org: string) => Promise<any[]>;
    onRestore: (id: number | string, type?: string) => Promise<any[]>;
    ops?: { read?: string; restore?: string; delete?: string };
    accessor: string;
    icon?: any;
};

export const ArchiveBlock = ({
    label,
    onDelete,
    onRestore,
    getter,
    ops,
    accessor,
}: // icon,
Props) => {
    const { user } = useContext(UserContext);
    const [getFeedback, setGetFeedback] = useState({
        status: APIRequestStatus.idle,
        message: "",
    });
    const [deleteFeedback, setDeleteFeedback] = useState({
        status: APIRequestStatus.idle,
        message: "",
    });
    const [restoreFeedback, setRestoreFeedback] = useState({
        status: APIRequestStatus.idle,
        message: "",
        itemId: null,
    });
    const [deleteItem, setDeleteItem] = useState<any>({
        item: null,
        open: false,
    });
    const theme = useTheme();
    const [rows, setRows] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        count: 0,
    });
    const [changes, setChanges] = useState(0);
    const refreshRef = useRef<any>(null);

    const [organizationCode, setOrganizationCode] = useState("");

    useEffect(() => {
        if (user?.user_type === User_Type.Admin) {
            setOrganizationCode(user?.organization?.code);
        }
    }, [user?.user_type]);

    const handleDeleteItem = async () => {
        setDeleteFeedback({
            status: APIRequestStatus.loading,
            message: "Deleting item ...",
        });

        if (deleteItem.item) {
            const [data, err] = await onDelete(
                deleteItem.item,
                organizationCode
            );

            if (data) {
                setDeleteFeedback({
                    status: APIRequestStatus.success,
                    message: data.message ?? "Item deleted successfully",
                });
                setChanges((n) => n + 1);
            } else {
                setDeleteFeedback({
                    status: APIRequestStatus.error,
                    message: err ?? "Item deletion failed",
                });
            }
        }
    };

    const handleRestoreItem = async (item: any) => {
        setRestoreFeedback({
            status: APIRequestStatus.loading,
            message: "Restoring item ...",
            itemId: item.id,
        });

        const [data, err] = await onRestore(item, organizationCode);

        if (data) {
            setRestoreFeedback({
                status: APIRequestStatus.success,
                message: data.message ?? "Item restored successfully",
                itemId: item.id,
            });
            setChanges((n) => n + 1);
        } else {
            setRestoreFeedback({
                status: APIRequestStatus.error,
                message: err ?? "Item restoration failed",
                itemId: item.id,
            });
        }
    };

    const getRows = async () => {
        if (!organizationCode) return;
        setGetFeedback({
            status: APIRequestStatus.loading,
            message: "Getting items ...",
        });

        const [data, err] = await getter({
            page: pagination.page,
            limit: pagination.limit,
            filter: {},
            org: organizationCode,
        });

        if (data) {
            console.log(data.count);
            setRows(data.rows);
            setPagination({ ...pagination, count: data.count });
            setGetFeedback({
                status: APIRequestStatus.success,
                message: data.message ?? "Items retireved successfully",
            });
        } else {
            setGetFeedback({
                status: APIRequestStatus.error,
                message: err ?? "Item retrieval failed",
            });
        }
    };

    const handleRefresh = () => {
        setChanges((n) => n + 1);
        if (refreshRef.current) {
            refreshRef.current.style.transform = "rotate(180deg)";
            refreshRef.current.style.transition = "1s all ease";

            setTimeout(() => {
                refreshRef.current!.style.transform = "";
                refreshRef.current!.style.transition = "";
            }, 1000);
        }
    };

    useEffect(() => {
        getRows();
    }, [pagination.page, pagination.limit, changes, organizationCode]);

    return (
        <Authorize opName={ops?.read}>
            <List dense>
                {/* <Alert>
                <AlertTitle></AlertTitle>
            </Alert> */}
                {getFeedback.status === APIRequestStatus.error && (
                    <ListItem>
                        <Alert
                            style={{ width: "100%" }}
                            severity="error"
                            onClose={() =>
                                setGetFeedback({
                                    status: APIRequestStatus.idle,
                                    message: "",
                                })
                            }
                        >
                            {getFeedback.message}
                        </Alert>
                    </ListItem>
                )}

                {(deleteFeedback.status === APIRequestStatus.success ||
                    deleteFeedback.status === APIRequestStatus.error) && (
                    <ListItem>
                        <Alert
                            style={{ width: "100%" }}
                            severity={deleteFeedback.status as any}
                            onClose={() =>
                                setDeleteFeedback({
                                    status: APIRequestStatus.idle,
                                    message: "",
                                })
                            }
                        >
                            {deleteFeedback.message}
                        </Alert>
                    </ListItem>
                )}

                {(restoreFeedback.status === APIRequestStatus.success ||
                    restoreFeedback.status === APIRequestStatus.error) && (
                    <ListItem>
                        <Alert
                            style={{ width: "100%" }}
                            severity={restoreFeedback.status as any}
                            onClose={() =>
                                setRestoreFeedback({
                                    status: APIRequestStatus.idle,
                                    message: "",
                                    itemId: null,
                                })
                            }
                        >
                            {restoreFeedback.message}
                        </Alert>
                    </ListItem>
                )}

                <ListItem divider>
                    <ListItemText
                        primary={label}
                        primaryTypographyProps={{ variant: "h6" }}
                    />

                    <ListItemSecondaryAction
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        {getFeedback.status === APIRequestStatus.loading ? (
                            <CircularProgress size="1.25em" />
                        ) : null}
                        <Pagination
                            count={
                                Math.trunc(
                                    pagination.count / pagination.limit
                                ) + 1
                            }
                            page={pagination.page}
                            onChange={(ev, _page) =>
                                setPagination({ ...pagination, page: _page })
                            }
                        />
                        <IconButton
                            size="small"
                            onClick={handleRefresh}
                            title="Refresh results"
                        >
                            <Cached ref={refreshRef} />
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>

                {rows.map((archiveItem: any) => (
                    <ListItem
                        key={archiveItem.id}
                        style={{
                            paddingLeft: theme.spacing(4),
                            cursor: "pointer",
                        }}
                    >
                        <Typography
                            variant="body1"
                            style={{ fontFamily: "monospace" }}
                        >
                            {` ${archiveItem[accessor]} - (${archiveItem.id})`}
                        </Typography>
                        <ListItemSecondaryAction>
                            <Authorize opName={ops?.restore}>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        handleRestoreItem(archiveItem)
                                    }
                                    title="Restore"
                                >
                                    {restoreFeedback.itemId ===
                                        archiveItem.id &&
                                    restoreFeedback.status ===
                                        APIRequestStatus.loading ? (
                                        <CircularProgress size="1em" />
                                    ) : (
                                        <UnarchiveOutlined />
                                    )}
                                </IconButton>
                            </Authorize>

                            <Authorize opName={ops?.delete}>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setDeleteItem({
                                            item: archiveItem,
                                            open: true,
                                        });
                                    }}
                                    title="Delete forever"
                                >
                                    {deleteItem.item?.id === archiveItem.id &&
                                    deleteFeedback.status ===
                                        APIRequestStatus.loading ? (
                                        <CircularProgress size="1em" />
                                    ) : (
                                        <DeleteForeverOutlined />
                                    )}
                                </IconButton>
                            </Authorize>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}

                {Boolean(deleteItem) && (
                    <ConfirmDelete
                        open={deleteItem.open}
                        setOpen={(v: boolean) =>
                            setDeleteItem({ ...deleteItem, open: v })
                        }
                        next={handleDeleteItem}
                    />
                )}
            </List>
        </Authorize>
    );
};
